// src/pages/Profile.js
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Upload,
  Delete,
  Save,
  Logout as LogoutIcon,
  CheckCircle,
  HourglassEmpty,
  Publish,
} from "@mui/icons-material";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import PublishConsentDialog from "./PublishConsentDialog";
import DashboardLayout from "../components/DashboardLayout";

const ProfilePageContent = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const [profile, setProfile] = useState({
    username: "",
    full_name: "",
    bio: "",
    avatar_url: "",
    notify_email: true,
    allow_anonymous_default: false,
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);

  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [initialProfile, setInitialProfile] = useState(null);
  const [openSaveConfirm, setOpenSaveConfirm] = useState(false);
  const [openDiscardConfirm, setOpenDiscardConfirm] = useState(false);
  const [pendingPath, setPendingPath] = useState(null); // where to go after confirming discard

  const publicAvatarUrl = useMemo(() => {
    if (!profile?.avatar_url) return null;
    const { data } = supabase.storage
      .from("avatars")
      .getPublicUrl(profile.avatar_url);
    return data?.publicUrl ?? null;
  }, [profile?.avatar_url]);

  const isDirty = useMemo(() => {
    if (!initialProfile) return false;
    const keys = [
      "username",
      "full_name",
      "bio",
      "avatar_url",
      "notify_email",
      "allow_anonymous_default",
    ];
    const a = keys.map((k) => initialProfile?.[k] ?? "").join("|");
    const b = keys.map((k) => profile?.[k] ?? "").join("|");
    return a !== b;
  }, [initialProfile, profile]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);
      setSubmissionsLoading(true);
      try {
        const { data: row, error } = await supabase
          .from("profiles")
          .select(
            "id, username, full_name, bio, avatar_url, notify_email, allow_anonymous_default"
          )
          .eq("id", user.id)
          .single();

        if (error) {
          if (error.code === "PGRST116" || error.message?.includes("No rows")) {
            const defaultProfile = {
              id: user.id,
              username: "",
              full_name: "",
              bio: "",
              avatar_url: "",
              notify_email: true,
              allow_anonymous_default: false,
            };

            const { data: newProfile, error: insertError } = await supabase
              .from("profiles")
              .insert(defaultProfile)
              .select()
              .single();

            if (insertError) {
              console.error("Failed to create profile:", insertError);
              throw insertError;
            }

            setProfile(newProfile);
            setInitialProfile(newProfile);
          } else {
            throw error;
          }
        } else {
          setProfile(row);
          setInitialProfile(row);
        }

        const { data: reports, error: reportsError } = await supabase
          .from("issue_reports")
          .select("id, report_title, created_at, publication_status")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (reportsError) {
          console.error("Failed to fetch reports:", reportsError);
          setSnack({ open: true, msg: "Could not load your submissions." });
        } else {
          setSubmissions(reports || []);
        }
      } catch (e) {
        console.error(e);
        setSnack({ open: true, msg: "Failed to load profile." });
      } finally {
        setLoading(false);
        setSubmissionsLoading(false);
      }
    };
    load();
  }, [user]);

  const handleOpenConsentDialog = (report) => {
    setSelectedReport(report);
    setIsConsentDialogOpen(true);
  };
  const handleCloseConsentDialog = () => {
    setSelectedReport(null);
    setIsConsentDialogOpen(false);
  };
  const handlePublishSuccess = useCallback((reportId) => {
    setSubmissions((prevSubmissions) =>
      prevSubmissions.map((submission) =>
        submission.id === reportId
          ? { ...submission, publication_status: "pending_review" }
          : submission
      )
    );
    setSnack({
      open: true,
      msg: "Publication request submitted successfully.",
    });
  }, []);

  // --- form handlers (existing) ---
  const handleChange = (field) => (e) => {
    const value =
      e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const path = `${user.id}/${Date.now()}.${fileExt}`;

      const { data: files, error: listError } = await supabase.storage
        .from("avatars")
        .list(user.id);
      if (listError) throw listError;
      if (files.length > 0) {
        const toDelete = files.map((f) => `${user.id}/${f.name}`);
        await supabase.storage.from("avatars").remove(toDelete);
      }

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, file, { cacheControl: "3600", upsert: false });

      if (uploadError) throw uploadError;

      setProfile((prev) => ({ ...prev, avatar_url: path }));
      setSnack({ open: true, msg: "Avatar uploaded. Don't forget to Save." });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "Avatar upload failed." });
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .upsert({ id: user.id, ...profile })
        .select()
        .single();

      if (error) throw error;
      setProfile(data);
      setInitialProfile(data); // reset baseline after save
      setSnack({ open: true, msg: "Profile saved." });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "Failed to save profile." });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!profile?.avatar_url) return;
    try {
      await supabase.storage.from("avatars").remove([profile.avatar_url]);
      setProfile((prev) => ({ ...prev, avatar_url: "" }));
      setSnack({ open: true, msg: "Avatar removed. Save to apply." });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "Failed to remove avatar." });
    }
  };

  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        "This will permanently delete your profile data but not your authentication account. Continue?"
      )
    )
      return;
    try {
      await supabase.from("profiles").delete().eq("id", user.id);
      setSnack({ open: true, msg: "Profile data deleted." });
      onLogout?.();
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "Failed to delete profile data." });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 3, p: 3, bgcolor: "background.default" }}>
      {/* --- LEFT STACK (AVATAR, ACCOUNT, DANGER ZONE) --- */}
      <Stack spacing={3} sx={{ width: 320, flexShrink: 0 }}>
        {/* Avatar Card */}
        <Card elevation={2}>
          <CardHeader
            title="Your Avatar"
            sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2} alignItems="center">
              <Avatar
                src={publicAvatarUrl ?? undefined}
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: 48,
                  bgcolor: "secondary.main",
                }}
              >
                {profile?.full_name?.[0]?.toUpperCase() ||
                  user?.email?.[0]?.toUpperCase()}
              </Avatar>
              <Stack direction="row" spacing={1}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<Upload />}
                  disabled={avatarUploading}
                >
                  {avatarUploading ? "Uploading..." : "Upload"}
                  <input
                    type="file"
                    hidden
                    accept="image/png, image/jpeg"
                    onChange={handleAvatarPick}
                  />
                </Button>
                <Tooltip title="Remove current avatar">
                  <span>
                    <IconButton
                      onClick={handleDeleteAvatar}
                      disabled={!profile?.avatar_url}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Account Card */}
        <Card elevation={2}>
          <CardHeader
            title="Account"
            sx={{ bgcolor: "info.main", color: "info.contrastText" }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label="Email"
                value={user?.email ?? ""}
                variant="filled"
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                color="error"
                onClick={onLogout}
                sx={{ flex: 1 }}
              >
                Logout
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Danger Zone Card */}
        <Card variant="outlined" sx={{ borderColor: "error.main" }}>
          <CardHeader
            title="Danger Zone"
            sx={{ bgcolor: "error.main", color: "error.contrastText" }}
          />
          <Divider />
          <CardContent>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              This action cannot be undone. This will permanently delete your
              profile and remove all your data.
            </Typography>
            <Button
              fullWidth
              color="error"
              onClick={handleDeleteAccount}
              startIcon={<Delete />}
              variant="contained"
            >
              Delete profile data
            </Button>
          </CardContent>
        </Card>
      </Stack>

      {/* --- MAIN CONTENT PAPER --- */}
      <Paper elevation={2} sx={{ flex: 1, p: 3, borderRadius: 2 }}>
        {/* Profile Settings */}
        <Typography variant="h5" gutterBottom>
          Profile Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update your personal details here. Click save when you're done.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full name"
              value={profile.full_name || ""}
              onChange={handleChange("full_name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Username"
              value={profile.username || ""}
              onChange={handleChange("username")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Bio"
              multiline
              rows={4}
              value={profile.bio || ""}
              onChange={handleChange("bio")}
              placeholder="Tell us a little about yourself"
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>Email notifications</Typography>
              <Switch
                checked={!!profile.notify_email}
                onChange={handleChange("notify_email")}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>Post anonymously by default</Typography>
              <Switch
                checked={!!profile.allow_anonymous_default}
                onChange={handleChange("allow_anonymous_default")}
              />
            </Stack>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => {
              if (isDirty) {
                setPendingPath("/"); // where Cancel intended to go
                setOpenDiscardConfirm(true);
              } else {
                navigate("/");
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={saving}
            onClick={() => {
              if (!isDirty) return;
              setOpenSaveConfirm(true);
            }}
            sx={{ minWidth: 120 }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Stack>

        {/* --- SUBMISSIONS SECTION --- */}
        <Box mt={4}>
          <Divider />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Your Submissions
          </Typography>
          {submissionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          ) : submissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              You have not made any submissions yet.
            </Typography>
          ) : (
            <List dense sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
              {submissions.map((report) => (
                <ListItem
                  key={report.id}
                  divider
                  secondaryAction={(() => {
                    switch (report.publication_status) {
                      case "published":
                        return (
                          <Chip
                            icon={<CheckCircle />}
                            label="Published"
                            color="success"
                            size="small"
                          />
                        );
                      case "pending_review":
                        return (
                          <Chip
                            icon={<HourglassEmpty />}
                            label="Pending Review"
                            color="warning"
                            size="small"
                          />
                        );
                      case "rejected":
                        return (
                          <Chip label="Rejected" color="error" size="small" />
                        );
                      case "not_requested":
                      default:
                        return (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Publish />}
                            onClick={() => handleOpenConsentDialog(report)}
                          >
                            Request to Publish
                          </Button>
                        );
                    }
                  })()}
                >
                  <ListItemText
                    primary={report.report_title}
                    secondary={`Submitted on: ${new Date(
                      report.created_at
                    ).toLocaleDateString()}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* --- RENDER THE DIALOG (it will be invisible until opened) --- */}
      <PublishConsentDialog
        open={isConsentDialogOpen}
        onClose={handleCloseConsentDialog}
        report={selectedReport}
        user={user}
        onSuccess={handlePublishSuccess}
      />

      {/* Save confirmation (opens only when isDirty) */}
      <Dialog open={openSaveConfirm} onClose={() => setOpenSaveConfirm(false)}>
        <DialogTitle>Save changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have unsaved changes. Do you want to save them now?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSaveConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setOpenSaveConfirm(false);
              await handleSave();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard confirmation for navigation/Cancel */}
      <Dialog
        open={openDiscardConfirm}
        onClose={() => setOpenDiscardConfirm(false)}
      >
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have unsaved changes. If you leave now, your edits will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiscardConfirm(false)}>Stay</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setOpenDiscardConfirm(false);
              const target = pendingPath || "/";
              setPendingPath(null);
              navigate(target);
            }}
          >
            Discard &amp; Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.msg}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

const ProfilePage = () => {
  return <DashboardLayout children={<ProfilePageContent />} />;
};

export default ProfilePage;
